import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { Message } from "@ag-ui/client";
import { useFetcher } from "~/hooks/useFetcher";
import { useProjectAppsFeature } from "~/hooks/useProjectAppsFeature";
import { useSessionContextPanel } from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import { artifactPreviewsPath, type ArtifactPreview } from "~/services/artifacts";
import { isHtmlArtifact } from "./artifacts/HtmlArtifact";
import { latestTurnAgentId } from "./nextTurnAgent";
import { artifactRefNames } from "./remarkArtifactRefs";

export interface SessionArtifactApp {
  artifactId: string;
  title: string;
}

interface SessionArtifactAppsContextValue {
  refNames: string[];
  setRefNames: Dispatch<SetStateAction<string[]>>;
  turnAgentId: string | null;
  setTurnAgentId: Dispatch<SetStateAction<string | null>>;
}

// Holds the artifact names the session references and its latest turn's agent,
// above both `Chat` (which owns the messages) and the context sidebar (which
// lists the apps among them and publishes them).
const SessionArtifactAppsContext = createContext<SessionArtifactAppsContextValue | null>(null);

export const SessionArtifactAppsProvider = ({ children }: { children: React.ReactNode }) => {
  const [refNames, setRefNames] = useState<string[]>([]);
  const [turnAgentId, setTurnAgentId] = useState<string | null>(null);

  return (
    <SessionArtifactAppsContext.Provider
      value={{ refNames, setRefNames, turnAgentId, setTurnAgentId }}
    >
      {children}
    </SessionArtifactAppsContext.Provider>
  );
};

// Every artifact a message names with `[[name]]`, de-duped, in first-seen order.
// Which of them are apps is only known once each resolves to a preview.
function artifactRefNamesFromMessages(messages: ReadonlyArray<Message>): string[] {
  const names = new Set<string>();
  for (const m of messages) {
    const { role, content } = m as { role?: string; content?: unknown };
    if ((role === "assistant" || role === "user") && typeof content === "string") {
      artifactRefNames(content).forEach((name) => names.add(name));
    }
  }
  return [...names];
}

// Called from `Chat`, which owns the messages; publishes the referenced names
// and the latest turn's agent to the sibling sidebar.
export function useSessionArtifactAppsSync(messages: ReadonlyArray<Message>): void {
  const ctx = useContext(SessionArtifactAppsContext);
  const names = useMemo(() => artifactRefNamesFromMessages(messages), [messages]);
  const turnAgentId = latestTurnAgentId(messages);

  // Every streamed token yields a new array; keep the published one unless the
  // names changed, or the provider's re-render would re-run this effect forever.
  useEffect(() => {
    ctx?.setRefNames((prev) => (sameNames(prev, names) ? prev : names));
  }, [ctx, names]);

  useEffect(() => {
    ctx?.setTurnAgentId(turnAgentId);
  }, [ctx, turnAgentId]);
}

// The agent of the session's latest turn: the one its apps were built with, and
// so the one a published app calls its tools as.
export function useSessionTurnAgentId(): string | null {
  return useRequiredContext().turnAgentId;
}

function useRequiredContext(): SessionArtifactAppsContextValue {
  const ctx = useContext(SessionArtifactAppsContext);
  if (!ctx) {
    throw new Error("Session artifact apps must be read within a SessionArtifactAppsProvider");
  }
  return ctx;
}

function sameNames(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((name, i) => name === b[i]);
}

// The session's HTML artifacts, each openable as an app from the sidebar.
export function useSessionArtifactApps(): SessionArtifactApp[] {
  const ctx = useRequiredContext();
  const { conversationId } = useSessionContextPanel();
  const { enabled: isProjectAppsEnabled } = useProjectAppsFeature();
  // A new reference changes the key; keep the previous list showing meanwhile,
  // narrowed to names this session still references so another session's
  // apps never linger after a switch.
  const { data } = useFetcher<ArtifactPreview[]>(
    isProjectAppsEnabled && conversationId && ctx.refNames.length > 0
      ? artifactPreviewsPath(conversationId, ctx.refNames)
      : null,
    { keepPreviousData: true }
  );
  if (!isProjectAppsEnabled) {
    return [];
  }
  const referenced = new Set(ctx.refNames);
  return (data ?? []).flatMap((preview) =>
    preview.artifact_name !== null &&
    referenced.has(preview.artifact_name) &&
    isHtmlArtifact(preview)
      ? [{ artifactId: preview.artifact_id, title: preview.artifact_name }]
      : []
  );
}
